import { jwtDecode } from "jwt-decode";
import { AUTH_TOKEN, PRACTICE_TOKEN } from "./constants";

/*
 * The native app keeps one WebView per device, so when doctor A logs out and doctor B
 * logs in, everything A's session cached in localStorage is still here. Native injects
 * only B's authToken; the cached practiceToken, pm_id, b2c_id, doctor_name etc. are A's.
 * The practiceToken is the visible one: TatvaPractice opens with it and shows A's name
 * and clinics to B.
 *
 * This guard runs before the app renders and drops every user-scoped key whenever the
 * signed-in user differs from the one the cache belongs to.
 */

const SESSION_USER_KEY = "session_user_mobile";

// Written by native before page scripts, or identify the device. Never user-scoped.
const PRESERVED_KEYS = [
  AUTH_TOKEN,
  "app_type",
  "fromNative",
  "isTablet",
  "device_type",
  "device_info",
];

type MedEcoUser = { mobileNumber?: string };

const digitsOf = (mobile?: string | number) => String(mobile ?? "").replace(/\D/g, "");

// One side may carry a country code. National numbers run 9-10 digits (+27 is 9, +91 is
// 10), so the last 9 digits identify the number either way.
const mobileKey = (mobile?: string | number) => digitsOf(mobile).slice(-9);

const readAuthUser = (): MedEcoUser | null => {
  try {
    const raw = localStorage.getItem(AUTH_TOKEN);
    const token = raw ? JSON.parse(raw) : null;
    return token ? (jwtDecode(token) as { user?: MedEcoUser }).user ?? null : null;
  } catch {
    return null;
  }
};

const decodePracticeToken = (practiceToken: string) =>
  jwtDecode(practiceToken) as { exp?: number; result?: { mobile_no?: string | number } };

/** True only with positive evidence: the token names a different mobile number. */
const isIssuedToAnotherUser = (practiceToken: string, user: MedEcoUser | null) => {
  try {
    const tokenMobile = mobileKey(decodePracticeToken(practiceToken).result?.mobile_no);
    const userMobile = mobileKey(user?.mobileNumber);
    return !!tokenMobile && !!userMobile && tokenMobile !== userMobile;
  } catch {
    return false;
  }
};

/**
 * True when the cached practice token can be sent to TatvaPractice for this user:
 * it decodes, has not expired, and was not issued to a different mobile number.
 */
export const isPracticeTokenValidForUser = (
  practiceToken: string | null,
  user: MedEcoUser | null = readAuthUser()
) => {
  if (!practiceToken || !user) return false;
  try {
    const { exp } = decodePracticeToken(practiceToken);
    if (exp && exp * 1000 <= Date.now()) return false;
    return !isIssuedToAnotherUser(practiceToken, user);
  } catch {
    return false;
  }
};

const clearUserScopedStorage = () => {
  const preserved = PRESERVED_KEYS.map((key) => [key, localStorage.getItem(key)] as const);
  localStorage.clear();
  sessionStorage.clear();
  preserved.forEach(([key, value]) => {
    if (value !== null) localStorage.setItem(key, value);
  });
};

export const resetStorageOnUserChange = () => {
  try {
    const user = readAuthUser();
    // The mobile number is the one identity every token type carries. Without it there is
    // no evidence of a user change, and a false wipe would mint a new ZymedPedia session.
    const userKey = mobileKey(user?.mobileNumber);
    if (!userKey) return;

    const cachedUserKey = localStorage.getItem(SESSION_USER_KEY);
    const rawPracticeToken = localStorage.getItem(PRACTICE_TOKEN);
    let practiceToken: string | null = null;
    try {
      practiceToken = rawPracticeToken ? JSON.parse(rawPracticeToken) : null;
    } catch {
      practiceToken = rawPracticeToken;
    }

    // Caches written before this guard shipped have no SESSION_USER_KEY; for those a
    // practice token issued to a different mobile number is the evidence of a stale user.
    // An expired token alone is not: that is the same doctor, who keeps their cache.
    const userChanged = cachedUserKey
      ? cachedUserKey !== userKey
      : !!practiceToken && isIssuedToAnotherUser(practiceToken, user);

    if (userChanged) clearUserScopedStorage();
    localStorage.setItem(SESSION_USER_KEY, userKey);
  } catch (error) {
    console.error("Session guard failed:", error);
  }
};
